import type { GraphData, ValidatedFormFields } from './types';

/** Months per year for CD calculator interest accrual (monthly). */
const CD_MONTHS_PER_YEAR = 12;

/**
 * Monthly rate implied by APY with monthly compounding.
 * APY is the effective annual yield: (1 + r_m)^12 = 1 + APY.
 */
export function apyToMonthlyRate(apyAsDecimal: number): number {
  if (apyAsDecimal <= 0) {
    return 0;
  }
  return (1 + apyAsDecimal) ** (1 / CD_MONTHS_PER_YEAR) - 1;
}

const DEFAULT_TERM_MONTHS_MIN = 1;
const DEFAULT_TERM_MONTHS_MAX = 120;

/**
 * Converts CD term length in days (e.g. `cdDepositTerm` from offers) to a whole number of months
 * for default form state when the UI defaults to the **months** unit.
 * Uses ~30 days per month, clamped to the calculator’s allowed month range.
 */
export function defaultTermDaysToTimePeriodMonths(days: number): string {
  if (!Number.isFinite(days) || days <= 0) {
    return String(DEFAULT_TERM_MONTHS_MIN);
  }
  const approximateMonths = Math.round(days / 30);
  const clamped = Math.min(
    DEFAULT_TERM_MONTHS_MAX,
    Math.max(DEFAULT_TERM_MONTHS_MIN, approximateMonths),
  );
  return String(clamped);
}

/**
 * Project CD maturity value from a lump-sum deposit (monthly compounding, no contributions).
 * `annualPercentageYield` is treated as **APY** (effective annual yield): after 12 months,
 * balance grows by exactly that annual factor before any longer-term projection.
 *
 * @param termLength - Term length as entered (years when `termLengthUnit` is years, months when months)
 */
export type CalculateCdInterestOptions = {
  /**
   * National average APY in percent points (e.g. `0.38` for 0.38%). When omitted or invalid, the graph
   * is interest-only at the entered CD APY (no national split or initial-deposit bar).
   */
  nationalAvgApy?: number;
};

export function calculateCdInterest(
  { initialDeposit, annualPercentageYield, termLength, termLengthUnit }: ValidatedFormFields,
  options?: CalculateCdInterestOptions,
) {
  const rateAsDecimal = annualPercentageYield / 100;

  const monthlyRateUser = apyToMonthlyRate(rateAsDecimal);
  const nationalAvgApyRaw = options?.nationalAvgApy;
  const hasNationalAverageApy =
    nationalAvgApyRaw != null && Number.isFinite(nationalAvgApyRaw) && nationalAvgApyRaw > 0;
  const monthlyRateNational = hasNationalAverageApy
    ? apyToMonthlyRate(nationalAvgApyRaw / 100)
    : null;

  const totalMonths =
    termLengthUnit === 'years'
      ? Math.round(termLength * CD_MONTHS_PER_YEAR)
      : Math.round(termLength);

  let balanceUser = initialDeposit;
  let balanceNational = initialDeposit;

  const data: GraphData[] = [];

  /** Below 49 months: one point per month; 49+ months: one point every two months (final month always included). */
  const monthStep = totalMonths >= 49 ? 2 : 1;

  for (let month = 1; month <= totalMonths; month++) {
    balanceUser *= 1 + monthlyRateUser;
    if (monthlyRateNational != null) {
      balanceNational *= 1 + monthlyRateNational;
    }

    const isStepMonth = month % monthStep === 0;
    const isFinalOddMonth = month === totalMonths && totalMonths % monthStep !== 0;
    if (!isStepMonth && !isFinalOddMonth) {
      continue;
    }

    const cdTotalInterest = Math.max(0, balanceUser - initialDeposit);
    let nationalAverage: number;
    let interestEarnedStack: number;
    if (hasNationalAverageApy && monthlyRateNational != null) {
      const nationalInterest = Math.max(0, balanceNational - initialDeposit);
      const nationalInterestShown = Math.min(nationalInterest, cdTotalInterest);
      nationalAverage = nationalInterestShown;
      interestEarnedStack = cdTotalInterest - nationalInterestShown;
    } else {
      nationalAverage = 0;
      interestEarnedStack = cdTotalInterest;
    }

    data.push({
      monthIndex: month,
      name: String(month),
      nationalAverage,
      interestEarned: interestEarnedStack,
    });
  }

  const total = balanceUser;
  const interestEarned = total - initialDeposit;
  const nationalAverageInterestEarned =
    hasNationalAverageApy && monthlyRateNational != null
      ? Math.max(0, balanceNational - initialDeposit)
      : 0;

  return {
    graphData: data,
    initialDeposit,
    interestEarned,
    nationalAverageInterestEarned,
    total,
  };
}

export function transformValuesToString({
  initialDeposit,
  annualPercentageYield,
  termLength,
  termLengthUnit,
}: ValidatedFormFields) {
  return {
    initialDeposit: initialDeposit.toLocaleString('en-US'),
    annualPercentageYield: annualPercentageYield.toLocaleString('en-US'),
    termLength: termLength.toLocaleString('en-US'),
    termLengthUnit,
  };
}
